// 全ボス共通の「体幹と体力の基準」(2026-10-09 比較版 → 2026-10-10 ユーザー採用で既定 = 個性つき (mode 2))。
//   DEV ?econBase=0|off = 前の量 (基準なし・個性なし) / =1 = 基準だけ / =2 または指定なし = 基準 + 個性 (既定)。配布版は URL を読まない。
//   10-10 ユーザー (検収パック A-1〜A-3): 基準 採る・介錯の個性 (体幹×1.3) 採る・覚醒王の個性 (体力×1.3) 採る +
//   「その分体幹のたまりやすさは1.3倍にしてみてほしい」→ 覚醒王だけ、こちらの行動でボスの体幹が溜まる量を ×1.3 (gainMul・下の ECON_BASE_PERSONALITY)。
//   ユーザー (10-09 試遊の後): 「最高効率レベルで戦闘していた場合、HPが50－60％くらいでボスの体幹を溜めきれるくらいに調整したい。
//   これをベースとして、HPが多いボスや体幹が強いボスが存在するという調整の仕方をしたい」
//   窓口の確認: 50〜60% は「幕ごとの体力」(どの幕も、その幕の体力が半分〜6割残った所で崩れる)。
//   念押し: 「HPが50－60％になったら体幹に関係なく崩れる」仕組みではない。崩れの条件は今どおり体幹の満タンだけ。
//     数字を動かして、最高効率のペースで戦うと結果としてそこで満タンになる形にする。
//   測る難易度は初演 (ユーザー 10-09「一旦初演だけでいいよ」)。
//
// 動かす物 = **ボスの体幹の量を、幕ごとに**。体力・主人公の弾きと殴りの量・体幹の戻りは変えない。
//   理由 (詳しくは docs/research/2026-10-09-econ-baseline-posture-hp-status.md):
//   - 主人公の量は全ボス共通 = 1つの数ではボスごとの拍の密度の差を吸えない (今の崩れの位置は 0〜70% とばらばら)。
//   - 体力を基準の摘みにすると、個性の「HPが多いボス」と同じ摘みを2つの意味で使うことになる。
//   - 体幹の量なら「基準 = 幕ごとの倍率 (表)」「体幹が強いボス = その上に掛ける1つの倍率」「HPが多いボス = 体力の倍率」と、
//     基準と個性の摘みが別々に立つ。
//
// 測り方・表の作り方 = tests/combat_sim/econ_baseline_measure.test.ts (ECON_BASE_FIT=1 で表を作り直す)。
// 騎士の2幕 (10-10 から配布版の既定・旧3幕は DEV ?knightActs=3) は、下の表の 'vigil-knight' の 2: [..] が効く (幕の数ごとに持つ・同じ物差しで合わせ済み)。

export type EconBaseMode = 0 | 1 | 2

/** ボスID → 幕の数 → 幕ごとの体幹の倍率 (今の maxPosture に掛ける)。 */
export type EconBaseTable = Record<string, Record<number, readonly number[]>>

/**
 * ボスの個性 (mode 2 = 既定だけ)。hpMul = 体力 (全幕) に掛ける / postureMul = 体幹の最大 (全幕) に掛ける /
 * gainMul = こちらの行動 (弾き・殴り・見切り反撃・弾き返した飛び道具・致命を弾いた時) でボスの体幹が溜まる量に掛ける。
 */
export type EconPersonality = { hpMul?: number; postureMul?: number; gainMul?: number }

// 基準の表 (初演・最高効率の自動プレイで、幕の体力が 50〜60% 残った所で体幹が満タン)。
//   値は ECON_BASE_FIT=1 の測定で決めた (研究文書の表)。未登録のボス・幕の数が合わない時は 1 (今のまま)。
//   今の値で測った「満タンの時の幕の体力の残り」(初演・justParryBot・16戦の平均) を右に書く。倍率 >1 = 今は崩れるのが早すぎる幕。
//   体幹の量 (今の maxPosture × 倍率) = 戴冠獣 290/427・騎士 141/234/306 (2幕版 370/340)・花嫁 122/472/523・介錯 259/317・覚醒王 581/508/261 (10-27 広い舞台＋噛みの間合い keep に合わせ直した)。
export const ECON_BASE_POSTURE_SCALE: EconBaseTable = {
  'nameless-shade': { 1: [1.0] },                     // 今 55%  (稽古の相手。今のまま基準の中)
  'dream-hound': { 2: [1.14, 1.68] },                 // 今 61% / 73%
  'vigil-knight': {
    3: [0.47, 0.78, 1.02],                            // 今 17% / 43% / 56%  (ユーザーが「ほぼ同じ」と感じた騎士。1幕目がいちばん遅い)
    2: [0.86, 0.79],                                  // 2幕 (配布版の既定・体幹 430): 今 48% / 43% → 体幹 370 / 340
  },
  'bride': { 3: [0.48, 1.85, 2.05] },                 // 今 25% / 76% / 81%
  'the-executioner': { 2: [0.72, 0.88] },             // 今 37% / 49%
  'crowned-king-awoken': { 3: [1.60, 1.40, 0.72] },   // 今 73% / 70% / 54%。10-27 広い舞台＋keep で [1.88, 1.60, 1.0] が 47 / 50 / 46% に下がったので合わせ直した (32戦: 幕1 は 1.60 で 53%・幕2/3 は 1.40 / 0.72 で 54 / 55%)
}

// 個性 (mode 2 = 既定・2026-10-10 ユーザー採用)。?econBase=1 は全員が基準どおり。
//   介錯は「体幹が強い」(重い道具・戻りの遅いボス。今も 37% / 49% と体幹が遅い側) = 体幹の最大 ×1.3。
//   覚醒王は「HPが多い」(最後の獣・今も体力 525 で全ボス最大) = 体力 ×1.3。ユーザー「その分体幹のたまりやすさは1.3倍にしてみてほしい」 =
//   こちらの行動で溜まる体幹も ×1.3 (gainMul)。体幹の最大を割るのではなく、溜まる量に掛ける
//   (理由 = 既存の「溜まる量の倍率」parryGainHpMul と同じ入口に載せられ、体幹の戻る速さ・猶予・HUD の満タンの割合を動かさない)。
//   ほかのボスは基準どおり (騎士は今回の注文の元 = 基準そのものを体験させる・戴冠獣は最初の本ボス = 基準を教える)。
export const ECON_BASE_PERSONALITY: Record<string, EconPersonality> = {
  'the-executioner': { postureMul: 1.3 },
  'crowned-king-awoken': { hpMul: 1.3, gainMul: 1.3 },
}

let mode: EconBaseMode = 2
let tableOverride: EconBaseTable | null = null
let personalityOverride: Record<string, EconPersonality> | null = null

/** '0' / 'off' (または数 0) = 前の量・'1' = 基準だけ・'2' / 指定なし / それ以外 = 基準 + 個性 (既定)。 */
export function setEconBaseMode(value: string | number | null | undefined): void {
  if (value === null || value === undefined || value === '') { mode = 2; return }
  if (value === 'off') { mode = 0; return }
  const n = Number(value)
  mode = n === 0 ? 0 : n === 1 ? 1 : 2
}

export function econBaseMode(): EconBaseMode {
  return mode
}

/** 表を差し替える (測定・騎士の2幕版の合わせ込み用)。null で既定の表へ戻す。 */
export function setEconBaseTable(table: EconBaseTable | null): void {
  tableOverride = table
}

/** 個性の表を差し替える (測定用)。null で既定へ戻す。 */
export function setEconBasePersonality(table: Record<string, EconPersonality> | null): void {
  personalityOverride = table
}

export function econBaseTable(): EconBaseTable {
  return tableOverride ?? ECON_BASE_POSTURE_SCALE
}

export function econBasePersonalityTable(): Record<string, EconPersonality> {
  return personalityOverride ?? ECON_BASE_PERSONALITY
}

/** こちらの行動でボスの体幹が溜まる量の個性の倍率。mode 2 (既定) の覚醒王だけ 1.3・それ以外は 1。combat.ts の bossPostureGainMul が呼ぶ。 */
export function econBaseGainMul(bossId: string): number {
  return mode === 2 ? econBasePersonalityTable()[bossId]?.gainMul ?? 1 : 1
}

/**
 * ボスを作る時の体力と、幕ごとの体幹の量。mode 0 (?econBase=0) の時は渡した値をそのまま返し、actMaxPostures は undefined。
 *   state.ts createBossFromDefinition だけが呼ぶ。幕が進んだ時の差し替えは combat.ts resolveExecuteHit (actMaxPostures を読む)。
 */
export function econBaseShape(bossId: string, roomCount: number, maxHp: number, maxPosture: number):
  { maxHp: number; actMaxPostures: number[] | undefined } {
  if (mode === 0) return { maxHp, actMaxPostures: undefined }
  const n = Math.max(1, roomCount)
  const scales = econBaseTable()[bossId]?.[n]
  const persona = mode === 2 ? econBasePersonalityTable()[bossId] : undefined
  const pm = persona?.postureMul ?? 1
  const hm = persona?.hpMul ?? 1
  const actMaxPostures = Array.from({ length: n }, (_, i) => Math.max(1, Math.round(maxPosture * (scales?.[i] ?? 1) * pm)))
  return { maxHp: Math.max(1, Math.round(maxHp * hm)), actMaxPostures }
}
